import React, { useMemo, useState } from 'react';
import SetRow from './SetRow.jsx';
import SportEval from './SportEval.jsx';
import FullScreenSheet from '../FullScreenSheet.jsx';
import { SketchUnderline } from '../sketch/index.jsx';
import { findPreviousSession } from '../../utils/evaluation.js';
import { MODE_SINGLE, countSportProgress } from '../../utils/sport.js';

const TAB_LOG = 'log';
const TAB_EVAL = 'eval';

/** Tab strip — only rendered when the habit has more than one tab to offer. */
function Tabs({ tab, onChange }) {
  const tabs = [
    { value: TAB_LOG, label: 'today' },
    { value: TAB_EVAL, label: 'progress' },
  ];

  return (
    <div role="tablist" className="mb-4 flex gap-5">
      {tabs.map((entry) => {
        const active = tab === entry.value;
        return (
          <button
            key={entry.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(entry.value)}
            className={`flex flex-col gap-0.5 pb-0.5 text-xs uppercase tracking-widest transition-colors ${
              active ? 'text-accent' : 'text-ink-soft hover:text-ink'
            }`}
          >
            {entry.label}
            {/* The active tab is marked with an inked squiggle, not a bar */}
            <SketchUnderline className={active ? 'text-accent' : 'text-transparent'} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * The full-page window you get by tapping a sport habit.
 *
 * "today" holds one row of numeric inputs per set. Values are saved as you
 * type — there is no save button, and ✕ simply closes. Once every field holds
 * a number the occurrence is ticked off, which the footer previews live.
 *
 * Two shortcuts save typing: filling the first set of a station copies those
 * numbers into its remaining sets (see autofillSets), and each field shows what
 * the last session recorded as a faint placeholder.
 *
 * "progress" only exists for habits created with tracking switched on, and
 * charts every session ever logged rather than just this one.
 *
 * @param {{habit: object, date: string, baseDate: string}} occurrence
 * @param {Record<string, string>} values current log for this occurrence
 * @param {Record<string, object>} sportLogs every log, for the progress tab
 * @param {(fieldKey: string, value: string) => void} onChange
 * @param {() => void} onClose
 */
export default function SportSession({ occurrence, values, sportLogs, onChange, onClose }) {
  const { habit, date, baseDate } = occurrence;
  const { sport } = habit;
  const single = sport.mode === MODE_SINGLE;
  const [tab, setTab] = useState(TAB_LOG);

  // Last time round — its numbers show as placeholders, not as real values.
  const previousValues = useMemo(
    () => findPreviousSession(habit, sportLogs, baseDate)?.values || {},
    [habit, sportLogs, baseDate]
  );

  const { filled, total } = countSportProgress(sport, values);
  const complete = total > 0 && filled === total;
  const showEval = Boolean(sport.evaluate);

  return (
    <FullScreenSheet
      title={habit.name}
      subtitle={date}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-3">
          <span
            className={`font-hand text-xl leading-none ${complete ? 'text-done' : 'text-ink-soft'}`}
          >
            {complete ? 'all done' : `${filled} / ${total} filled in`}
          </span>

          <button
            type="button"
            onClick={onClose}
            className={`ml-auto rounded-pill border px-4 py-1.5 text-[10px] uppercase tracking-widest
                        transition-colors ${
                          complete
                            ? 'border-done bg-done text-white'
                            : 'border-line text-ink-soft hover:border-ink'
                        }`}
          >
            done
          </button>
        </div>
      }
    >
      {showEval && <Tabs tab={tab} onChange={setTab} />}

      {tab === TAB_EVAL && showEval ? (
        <SportEval habit={habit} sportLogs={sportLogs} />
      ) : (
        <div className="flex flex-col gap-4">
          {sport.stations.map((station) => (
            <section key={station.id} className="panel-hand flex flex-col p-3">
              {/* One-step activities have no station heading — the title says it */}
              {!single && (
                <h3 className="pb-1.5 font-display text-sm uppercase tracking-tight">
                  {station.name || 'station'}
                  <SketchUnderline className="text-line" />
                </h3>
              )}

              {Array.from({ length: station.sets }, (unused, setIndex) => (
                <SetRow
                  key={setIndex}
                  label={single ? station.name || habit.name : `set ${setIndex + 1}`}
                  stationId={station.id}
                  setIndex={setIndex}
                  metrics={station.metrics}
                  values={values}
                  previousValues={previousValues}
                  onChange={onChange}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </FullScreenSheet>
  );
}
